import React, { useRef } from 'react';
import { AvCheckbox, AvTooltip } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box, Divider, Skeleton } from '@mui/material';
import { AvalorSelectLoadingValue, DIVIDER_OPTION, selectOptionInteractionStyle } from './constants';

/**
 * When filterFunc (server-side search) is provided, the options are stale between
 * the moment search changes and the moment loading finishes. This hook tracks when
 * loading transitions from true → false (results arrived) and marks the search as
 * settled, so the label doesn't flash "Select Matching" with stale data.
 */
export const useOptionsMatchSearch = (search: string, loading: boolean, hasServerSearch: boolean) => {
  const prevLoadingRef = useRef(loading);
  const settledSearchRef = useRef('');
  if (hasServerSearch && prevLoadingRef.current && !loading) {
    settledSearchRef.current = search;
  }
  if (!hasServerSearch) {
    settledSearchRef.current = search;
  }
  prevLoadingRef.current = loading;
  return settledSearchRef.current === search;
};

const getLoadingOption = () => (
  <Box sx={{ 'li:has(> &)': { pointerEvents: 'none' }, ...flex.col, gap: 0.5, px: '12px' }}>
    <Skeleton width={120} height={16} />
    <Skeleton width={220} height={16} />
    <Skeleton width={100} height={16} />
    <Skeleton width={180} height={16} />
    <Skeleton width={120} height={16} />
    <Skeleton width={220} height={16} />
    <Skeleton width={100} height={16} />
    <Skeleton width={180} height={16} />
  </Box>
);

export const getDefaultValue = (isMultiple: boolean): any[] | null => (isMultiple ? [] : null);

const renderWithCheckbox = (renderOption, renderOptionStyle, options) =>
  function renderOpt(props, opt, { selected, index }) {
    if ((index === 0 || index === options.length - 1) && opt.value === DIVIDER_OPTION) {
      return null;
    }

    if (opt.value === DIVIDER_OPTION) {
      return <Divider key={DIVIDER_OPTION} sx={{ m: '8px' }} />;
    }

    if (opt.value === AvalorSelectLoadingValue) {
      return getLoadingOption();
    }

    return (
      <Box
        component="li"
        sx={[selectOptionInteractionStyle, renderOptionStyle(opt)]}
        {...props}
        key={opt.value}
        className={`${props.className} has-checkbox`}>
        <AvCheckbox value={selected} />
        <AvTooltip sx={{ width: '100%' }}>{renderOption(opt)}</AvTooltip>
      </Box>
    );
  };

export const getRenderOptionFunc = ({ renderOption, isMultiple, getLabelFunc, renderOptionStyle, skeletonLoading, options }) => {
  if (isMultiple) {
    return renderWithCheckbox(renderOption || getLabelFunc, renderOptionStyle, options);
  }
  if (skeletonLoading) {
    return function loader(props, opt) {
      if (opt.value === AvalorSelectLoadingValue) {
        return getLoadingOption();
      }
      return <li {...props}>{(renderOption || getLabelFunc)(opt)}</li>;
    };
  }
  return (
    renderOption &&
    ((props, opt) => (
      <li {...props} key={props.key}>
        {renderOption(opt)}
      </li>
    ))
  );
};
